import { getApiUrl } from '../utils/config';

const LOGIN_PATH = '/login';

/**
 * API Client for making HTTP requests
 */
class ApiClient {
  constructor() {
    this.baseUrl = getApiUrl();
    this.pendingRefresh = null;
  }

  /**
   * Try to refresh access token using refresh_token. Returns true if new token saved, false otherwise.
   * Deduplicates concurrent refresh calls.
   */
  async tryRefresh() {
    if (this.pendingRefresh) return this.pendingRefresh;
    this.pendingRefresh = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return false;
        const refreshUrl = `${this.baseUrl}/auth/refresh`;
        const res = await fetch(refreshUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.data?.token) {
          localStorage.setItem('auth_token', data.data.token);
          return true;
        }
        return false;
      } finally {
        this.pendingRefresh = null;
      }
    })();
    return this.pendingRefresh;
  }

  /**
   * Clear auth storage and redirect to login (e.g. when refresh fails or token invalid).
   */
  clearAuthAndRedirect() {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('account');
    window.location.href = LOGIN_PATH;
  }

  /**
   * Get default headers
   * @returns {Object} Default headers
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add authorization token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response
   * @returns {Promise} Parsed response data
   */
  async handleResponse(response) {
    let data;
    const contentType = response.headers.get('content-type');

    try {
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = text ? { error: { message: text } } : { error: { message: 'An error occurred' } };
      }
    } catch (parseError) {
      data = { error: { message: `Failed to parse response: ${parseError.message}` } };
    }

    if (!response.ok) {
      const error = {
        message: data.error?.message || `HTTP ${response.status}: ${response.statusText}`,
        code: data.error?.code || 'UNKNOWN_ERROR',
        status: response.status,
      };
      throw error;
    }

    return data;
  }

  /**
   * Make GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Additional options (responseType, etc.)
   * @returns {Promise} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http')
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;

    const url = new URL(fullUrl);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    const headers = this.getHeaders();

    // Remove Content-Type for blob responses
    if (options.responseType === 'blob') {
      delete headers['Content-Type'];
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers,
    });

    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.get(endpoint, params, { ...options, _isRetry: true });
      this.clearAuthAndRedirect();
      throw new Error('Session expired');
    }

    // Handle blob responses
    if (options.responseType === 'blob') {
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'download';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, filename };
    }

    return this.handleResponse(response);
  }

  /**
   * Make POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise} Response data
   */
  async post(endpoint, data = {}, options = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Construct full URL
    // If baseUrl is absolute (starts with http), use it directly
    // Otherwise, it's a relative path, prepend origin
    const fullUrl = this.baseUrl.startsWith('http')
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.post(endpoint, data, { _isRetry: true });
      this.clearAuthAndRedirect();
      throw new Error('Session expired');
    }

    return this.handleResponse(response);
  }

  /**
   * Make PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} options - Internal options (e.g. _isRetry)
   * @returns {Promise} Response data
   */
  async put(endpoint, data = {}, options = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http')
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;

    const response = await fetch(fullUrl, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.put(endpoint, data, { _isRetry: true });
      this.clearAuthAndRedirect();
      throw new Error('Session expired');
    }

    return this.handleResponse(response);
  }

  /**
   * Make DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Internal options (e.g. _isRetry)
   * @returns {Promise} Response data
   */
  async delete(endpoint, options = {}) {
    // Ensure endpoint starts with /
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Construct full URL with origin for relative paths
    const fullUrl = this.baseUrl.startsWith('http')
      ? `${this.baseUrl}${cleanEndpoint}`
      : `${window.location.origin}${this.baseUrl}${cleanEndpoint}`;

    const response = await fetch(fullUrl, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (response.status === 401 && !options._isRetry) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.delete(endpoint, { _isRetry: true });
      this.clearAuthAndRedirect();
      throw new Error('Session expired');
    }

    return this.handleResponse(response);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export health check function as example
export const healthService = {
  check: () => apiClient.get('/health'),
};
